import { ABOUT_CONTENT, CLINIC_INFO } from '../data';
import { Award, Shield, Heart, Clock, CheckCircle2, Phone, Mail, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function About() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Om mig";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-24 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/f9241ee8-22bb-46b1-86f5-27336afea1de.jpg"
            alt="Om Carsten Dalsgaard Hansen"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Om mig
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light max-w-xl">
              Mød Carsten Dalsgaard Hansen – din professionelle samarbejdspartner inden for fysiurgisk massage og velvære.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bio Section */}
      <section id="om-mig" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative max-w-sm sm:max-w-md lg:max-w-none mx-auto lg:mx-0"
            >
              <div className="aspect-[4/5] overflow-hidden rounded-[2rem] shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/1e24c603-6841-4d66-9428-7e480b71b0f4.jpg"
                  alt="Carsten Dalsgaard Hansen"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-emerald-600 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <p className="text-3xl font-bold mb-1">2012</p>
                <p className="text-xs font-medium uppercase tracking-wider">Selvstændig siden</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">Carsten Dalsgaard Hansen</h2>
              <p className="text-lg md:text-xl text-emerald-600 font-medium mb-8">Lægeeksamineret fysiurgisk sports massør</p>
              
              <div className="prose prose-sm sm:prose-base md:prose-lg text-gray-700 max-w-none space-y-6">
                <p>
                  Jeg er født i 1972 og uddannet Lægeeksamineret Fysiurgisk Sportsmassør tilbage i februar 2012, og selvstændig fra 1. september 2012.
                </p>
                <p>
                  Efterfølgende har jeg taget kurser i afspændingsmassage for gravide og smertelindring af gravide.
                </p>

                <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100 my-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    Jeg tilbyder følgende:
                  </h3>
                  <ul className="space-y-4 text-blue-800">
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 shrink-0" />
                      <span><strong>Fysiurgisk massage/dybdegående massage</strong> i forbindelse med smerter i ryg, nakke skuldre, lænd, arme og ben.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 shrink-0" />
                      <span><strong>Gravid massage.</strong> Afspændingsmassage og behandling af graviditetsrelaterede smerter.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 shrink-0" />
                      <span><strong>Wellness-/afspændingsmassage.</strong></span>
                    </li>
                  </ul>
                </div>

                <p className="font-medium text-base md:text-lg text-gray-800">
                  Så hvis du har brug for massage eller blot et ønske om det, så tøv ikke med at kontakte mig, eller book online her på hjemmesiden.
                </p>

                <div className="flex flex-col gap-4 pt-4">
                  <a 
                    href="tel:50933954" 
                    className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-2xl hover:border-emerald-500 hover:shadow-md transition-all group"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                      <Phone className="w-5 h-5 text-emerald-600 group-hover:text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Telefon</p>
                      <p className="text-blue-900 font-bold">50 93 39 54</p>
                    </div>
                  </a>
                  <a 
                    href="mailto:info@cdhmassage.dk" 
                    className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-2xl hover:border-blue-500 hover:shadow-md transition-all group"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                      <Mail className="w-5 h-5 text-blue-600 group-hover:text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Email</p>
                      <p className="text-blue-900 font-bold">info@cdhmassage.dk</p>
                    </div>
                  </a>
                </div>

                <div className="pt-6">
                  <Link 
                    to="/booking" 
                    className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3.5 lg:px-10 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 w-full sm:w-auto"
                  >
                    <Calendar className="w-5 h-5" />
                    Book online nu
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Min professionelle tilgang</h2>
            <p className="text-blue-200 text-sm sm:text-base max-w-2xl mx-auto">
              Hver behandling tager udgangspunkt i dine specifikke behov for at sikre det bedste resultat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ABOUT_CONTENT.approach.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-[2rem] hover:bg-white/10 transition-all"
              >
                <div className="bg-emerald-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-blue-100/70 text-[13px] sm:text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinic section */}
      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <h2 className="text-3xl font-bold text-blue-900 mb-6">Klinikken</h2>
              <p className="text-gray-600 mb-8 text-base md:text-lg">
                Her finder du lyse og indbydende lokaler, der er indrettet specifikt til at skabe ro og harmoni omkring din behandling. Jeg lægger vægt på en tryg atmosfære, hvor du er i centrum.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <Clock className="w-6 h-6 text-emerald-500 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-blue-900">Mange års erfaring</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Etableret in 2012 med fokus på faglighed og resultater.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Shield className="w-6 h-6 text-emerald-500 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-blue-900">Trygge rammer</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Et professionelt miljø hvor din komfort altid er førsteprioritet.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/b2e07219-119c-4724-b538-1eb3a7e289f8.jpg"
                  alt="Fra klinikken"
                  className="rounded-2xl shadow-lg h-64 w-full object-cover"
                />
                <img
                  src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/db4c2b7b-4a73-402f-b967-85c7da0c1756.jpg"
                  alt="Behandlingsrum"
                  className="rounded-2xl shadow-lg h-64 w-full object-cover mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
